import React, { useState } from "react";
import { View, ScrollView, StyleSheet, Alert, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { unionService } from "@/services/unionService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { UserPicker } from "@/components/Pickers/UserPicker";

// Union form data interface
interface UnionFormData {
  name: string;
  description?: string;
  meta_data?: Record<string, any>;
}

export default function UnionCreatePage() {
  const { colors } = useTheme();
  const { userRoles, tenantId } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"basic" | "users">("basic");

  const [unionData, setUnionData] = useState<UnionFormData>({
    name: "",
    description: "",
    meta_data: {},
  });

  const [users, setUsers] = useState<any[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);

  const permissions = getEntityPermissions(userRoles, "unions");

  // Check permissions
  if (!permissions.create) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centered}>
          <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
          <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>
            You don&apos;t have permission to create unions
          </ThemedText>
        </View>
      </View>
    );
  }

  const updateUnionData = (updates: Partial<UnionFormData>) => {
    setUnionData((prev) => ({ ...prev, ...updates }));
  };

  const handleUserSelection = (selectedUsers: any[]) => {
    setUsers(selectedUsers);
    setShowUserPicker(false);
  };

  const handleSave = async () => {
    if (!tenantId) {
      Alert.alert("Error", "Tenant ID not found");
      return;
    }

    setLoading(true);
    try {
      const newUnion = await unionService.addUnion({
        name: unionData.name,
        description: unionData.description,
        meta_data: unionData.meta_data,
      });

      if (newUnion?.id) {
        // Add users to the union if any were selected
        if (users.length > 0) {
          const userIds = users.map((u) => u.id!);
          await unionService.addUsersToUnion(newUnion.id, tenantId, userIds);
        }

        toast.showToast({
          title: "Union created",
          message: "Union has been successfully created",
          toastType: "success",
        });

        // Navigate to the new union's edit page
        router.replace(`/settings/unions/${newUnion.id}`);
      }
    } catch (error: any) {
      toast.showToast({
        title: "Creation failed",
        message: error?.message || "Failed to create union",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    router.push("/(app)/settings/unions");
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "basic":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <ThemedText style={styles.sectionTitle}>Basic Information</ThemedText>
              <ThemedInput
                label="Union Name *"
                value={unionData.name}
                onChangeText={(text) => updateUnionData({ name: text })}
                placeholder="Enter union name"
                style={styles.input}
              />
              <ThemedInput
                label="Description"
                value={unionData.description}
                onChangeText={(text) => updateUnionData({ description: text })}
                placeholder="Enter union description"
                multiline
                numberOfLines={3}
                style={styles.textArea}
              />
            </View>
          </View>
        );

      case "users":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <ThemedText style={styles.sectionTitle}>User Assignment ({users.length})</ThemedText>
                <Button
                  text="Select Users"
                  icon="account-plus"
                  onPress={() => setShowUserPicker(true)}
                  style={styles.actionButton}
                />
              </View>

              {users.length > 0 ? (
                <View style={styles.userList}>
                  {users.map((user) => {
                    const displayName = user.meta_data?.preferred_name || user.first_name;
                    const fullName = `${displayName} ${user.last_name}`.trim();

                    return (
                      <View key={user.id} style={[styles.userItem, { borderBottomColor: colors.border }]}>
                        <View style={styles.userInfo}>
                          <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
                          {user.email_address && (
                            <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>
                          )}
                          {user.meta_data?.roles && user.meta_data.roles.length > 0 && (
                            <ThemedText style={styles.userRole}>Roles: {user.meta_data.roles.join(", ")}</ThemedText>
                          )}
                        </View>
                        <TouchableOpacity
                          onPress={() => {
                            const updatedUsers = users.filter((u) => u.id !== user.id);
                            setUsers(updatedUsers);
                          }}
                          style={styles.removeButton}
                        >
                          <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="account-group-outline" size={48} color={colors.textSecondary} />
                  <ThemedText style={styles.emptyText}>No users assigned</ThemedText>
                  <ThemedText style={styles.emptySubtext}>Users assigned to this union will be members</ThemedText>
                </View>
              )}
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={handleCancel} style={styles.backButton}>
            <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
          </TouchableOpacity>
          <View>
            <ThemedText type="title">Create Union</ThemedText>
            <ThemedText style={styles.subtitle}>Configure a new union</ThemedText>
          </View>
        </View>
        <View style={styles.headerActions}>
          <Button text="Cancel" onPress={handleCancel} style={styles.cancelButton} />
          <Button
            text={loading ? "Creating..." : "Create Union"}
            onPress={handleSave}
            disabled={loading || !unionData.name.trim()}
            style={styles.saveButton}
          />
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabBar, { borderBottomColor: colors.border }]}>
        {[
          { key: "basic", label: "Basic Info", icon: "information-outline" },
          { key: "users", label: "Users", icon: "account-group" },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              {
                borderBottomColor: activeTab === tab.key ? colors.primary : "transparent",
              },
            ]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <MaterialCommunityIcons
              name={tab.icon as any}
              size={20}
              color={activeTab === tab.key ? colors.primary : colors.textSecondary}
            />
            <ThemedText
              style={[styles.tabText, { color: activeTab === tab.key ? colors.primary : colors.textSecondary }]}
            >
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderTabContent()}
      </ScrollView>

      {/* User Picker Modal */}
      {showUserPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <UserPicker
              multiple
              selectedUserIds={users.map((u) => u.id!)}
              onMultiPick={handleUserSelection}
              onCancel={() => setShowUserPicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  subtitle: {
    opacity: 0.7,
    marginTop: 2,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 16,
  },
  saveButton: {
    paddingHorizontal: 16,
  },
  tabBar: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    gap: 6,
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  content: {
    flex: 1,
  },
  tabContent: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
  },
  actionButton: {
    paddingHorizontal: 12,
  },
  input: {
    marginBottom: 12,
  },
  textArea: {
    marginBottom: 12,
    minHeight: 80,
  },
  userList: {
    gap: 0,
  },
  userItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  userRole: {
    fontSize: 11,
    opacity: 0.5,
    fontStyle: "italic",
  },
  removeButton: {
    padding: 4,
  },
  emptyState: {
    alignItems: "center",
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "500",
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 14,
    opacity: 0.7,
    textAlign: "center",
    marginTop: 4,
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
});
